const SELECTORS = {
    mainContent: '.main-content',
    readMore: '.wp-block-read-more',
}

const CLASSES = {
    active: 'read-more--active',
    expanded: 'read-more--expanded',
    hiddenContent: 'read-more__hidden-content',
    trigger: 'read-more__trigger',
    button: 'read-more__button',
}

// Module-level state — set by setupReadMore, read by expandIfHidden
let _mainContent = null
let _hiddenWrapper = null
let _triggerWrapper = null
let _button = null

/**
 * Collects all siblings after the read-more anchor, wraps them in a hidden
 * div, replaces the anchor with a trigger div containing the expand button.
 */
const setupReadMore = ( mainContent ) => {
    const readMoreLink = mainContent.querySelector( SELECTORS.readMore )
    if ( !readMoreLink ) { return }

    // Collect all siblings that come after the read-more link
    const hiddenChildren = []
    let sibling = readMoreLink.nextElementSibling
    while ( sibling ) {
        hiddenChildren.push( sibling )
        sibling = sibling.nextElementSibling
    }

    if ( hiddenChildren.length === 0 ) { return }

    // Wrap hidden children in a container
    const hiddenWrapper = document.createElement( 'div' )
    hiddenWrapper.classList.add( CLASSES.hiddenContent )
    hiddenWrapper.setAttribute( 'inert', '' )

    hiddenChildren.forEach( function( child ) {
        hiddenWrapper.appendChild( child )
    } )

    // Create a <button> to replace the <a> — preserves inner HTML (text + screen-reader span)
    const button = document.createElement( 'button' )
    button.classList.add( CLASSES.button )
    button.setAttribute( 'type', 'button' )
    button.setAttribute( 'aria-expanded', 'false' )
    button.innerHTML = readMoreLink.innerHTML

    // Trigger wrapper: holds the gradient (via ::before) and the button
    const triggerWrapper = document.createElement( 'div' )
    triggerWrapper.classList.add( CLASSES.trigger )
    triggerWrapper.appendChild( button )

    // Swap the original anchor for the trigger wrapper
    readMoreLink.parentNode.replaceChild( triggerWrapper, readMoreLink )

    // Append hidden content after trigger
    mainContent.appendChild( hiddenWrapper )

    // Mark main content as active so SCSS styles engage
    mainContent.classList.add( CLASSES.active )

    // Store references for external access via expandIfHidden
    _mainContent = mainContent
    _hiddenWrapper = hiddenWrapper
    _triggerWrapper = triggerWrapper
    _button = button

    button.addEventListener( 'click', function() {
        expand( mainContent, triggerWrapper, hiddenWrapper, button )
    } )
}

const expand = ( mainContent, triggerWrapper, hiddenWrapper, button, focusTarget ) => {
    mainContent.classList.add( CLASSES.expanded )
    button.setAttribute( 'aria-expanded', 'true' )

    // Hide the trigger (gradient + button) once expanded
    triggerWrapper.setAttribute( 'hidden', '' )

    // Re-enable interaction and AT visibility for the revealed content
    hiddenWrapper.removeAttribute( 'inert' )

    // Focus the specified target, or fall back to the first heading in revealed content
    const elementToFocus = focusTarget || hiddenWrapper.querySelector( 'h1, h2, h3, h4, h5, h6' )
    if ( elementToFocus ) {
        if ( !elementToFocus.getAttribute( 'tabindex' ) ) {
            elementToFocus.setAttribute( 'tabindex', '-1' )
        }
        elementToFocus.focus()
    }
}

/**
 * Checks whether targetElement lives inside the hidden content container.
 * If so — and if the content hasn't already been expanded — expands the
 * content and focuses the target element.
 *
 * Returns true if expansion occurred so the caller (e.g. a sticky nav module)
 * can handle its own scroll logic (offset for sticky header, smooth scroll, etc.).
 * Returns false if the target is already visible or not found in this section.
 *
 * Usage in a sticky nav module:
 *
 *   import { default as readMore } from "./_read-more"
 *
 *   link.addEventListener( 'click', function( e ) {
 *       const target = document.querySelector( link.getAttribute( 'href' ) )
 *       if ( readMore.expandIfHidden( target ) ) {
 *           e.preventDefault()
 *           // handle scroll with your own offset / behavior here
 *       }
 *   } )
 */
const expandIfHidden = ( targetElement ) => {
    if ( !_hiddenWrapper || !targetElement ) { return false }
    if ( !_hiddenWrapper.contains( targetElement ) ) { return false }
    if ( _mainContent.classList.contains( CLASSES.expanded ) ) { return false }

    expand( _mainContent, _triggerWrapper, _hiddenWrapper, _button, targetElement )

    return true
}

const init = () => {
    const mainContent = document.querySelector( SELECTORS.mainContent )
    if ( !mainContent ) { return }

    // If the URL has any hash, leave all content visible so the browser's
    // native anchor scroll works without interference. The read-more anchor
    // is already hidden via the PHP render filter so no JS action is needed.
    if ( window.location.hash ) { return }

    setupReadMore( mainContent )

    // Allow other scripts (e.g. article-nav view-script) to trigger expansion
    // via a custom event rather than a direct import.
    document.addEventListener( 'readmore:expand', function( e ) {
        var target = e.detail && e.detail.target ? e.detail.target : null
        expandIfHidden( target )
    } )
}

export default { init, expandIfHidden }